import React from 'react';
import { X, ExternalLink, CheckCircle } from 'lucide-react';

interface GoogleBusinessSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GoogleBusinessSetupModal({ isOpen, onClose }: GoogleBusinessSetupModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Google Business Profile Integration Setup</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              To connect Google Business Profile, you need to create a Google Cloud project and enable the required APIs.
              This gives you full control over your Google Business Profile integration.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                1
              </span>
              Create Google Cloud Project
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">
                    Go to{' '}
                    <a
                      href="https://console.cloud.google.com/projectcreate"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:underline inline-flex items-center"
                    >
                      Google Cloud Console
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Create a new project (e.g., "My Social Media Manager")</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Wait for the project to be created (a few seconds)</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                2
              </span>
              Add Billing Account (Required!)
            </h3>
            <div className="ml-11 space-y-3">
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  💳 Google requires a credit/debit card to use Google Business Profile API!
                </p>
                <p className="text-sm text-yellow-800 mb-2">
                  This is mandatory for identity verification and preventing spam. The API itself is <strong>100% FREE</strong> - you will NOT be charged for using it.
                </p>
                <p className="text-xs text-yellow-700">
                  Google gives you $300 free credit when you start. Without billing, quota will be 0 and API won't work.
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">
                    Go to{' '}
                    <a
                      href="https://console.cloud.google.com/billing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:underline inline-flex items-center"
                    >
                      Billing
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Link a billing account" or "Create billing account"</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Fill in your country, account type, and contact details</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Add your credit/debit card (Google may verify with $1 charge that's immediately refunded)</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Link your project to the billing account</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                3
              </span>
              Enable Required APIs
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Enable these APIs for your project:</p>
                  <ul className="list-disc list-inside ml-4 space-y-2 text-sm text-gray-600">
                    <li>
                      <a
                        href="https://console.cloud.google.com/apis/library/mybusiness.googleapis.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:underline inline-flex items-center"
                      >
                        Google My Business API
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://console.cloud.google.com/apis/library/mybusinessaccountmanagement.googleapis.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:underline inline-flex items-center"
                      >
                        My Business Account Management API
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://console.cloud.google.com/apis/library/mybusinessbusinessinformation.googleapis.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:underline inline-flex items-center"
                      >
                        My Business Business Information API
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Enable" for each API</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                4
              </span>
              Configure OAuth Consent Screen
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">
                    Go to{' '}
                    <a
                      href="https://console.cloud.google.com/apis/credentials/consent"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:underline inline-flex items-center"
                    >
                      OAuth consent screen
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Select "External" user type and click "Create"</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Fill in the required information:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-sm text-gray-600">
                    <li>App name: Your application name</li>
                    <li>User support email: Your email</li>
                    <li>Developer contact email: Your email</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Save and Continue"</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">On "Scopes" step, click "Add or Remove Scopes"</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Add these scopes:</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-xs text-gray-800 space-y-1">
                    <div>https://www.googleapis.com/auth/business.manage</div>
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Continue through the remaining steps and save</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                5
              </span>
              Create OAuth 2.0 Credentials
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">
                    Go to{' '}
                    <a
                      href="https://console.cloud.google.com/apis/credentials"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:underline inline-flex items-center"
                    >
                      Credentials page
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Create Credentials" → "OAuth client ID"</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Select "Web application" as application type</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Give it a name (e.g., "Social Media Manager Web")</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 mb-2">Under "Authorized redirect URIs", add this URL:</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-xs text-gray-800 break-all">
                    {import.meta.env.VITE_SUPABASE_URL || 'https://your-project.supabase.co'}/functions/v1/google-business-oauth
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    This is the Supabase edge function URL that handles the OAuth flow. The function will redirect to your app automatically.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Create"</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                6
              </span>
              Get Your Credentials
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">After creating, you'll see your credentials:</p>
              </div>
              <div className="ml-7 space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Client ID</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm text-gray-800">
                    Example: 787270739098-abc123xyz.apps.googleusercontent.com
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Client Secret</p>
                  <div className="bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm text-gray-800">
                    Example: GOCSPX-XXXXXXXXXXXXXXXXXXXXX
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Keep this secret secure and never share it publicly!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">
                7
              </span>
              Add Credentials to Your App
            </h3>
            <div className="ml-11 space-y-3">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Go to "API Settings" in this application</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Paste your Google Client ID and Client Secret</p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">Click "Save" and then try connecting your Google Business Profile!</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">Testing Mode</h4>
            <p className="text-sm text-yellow-800 mb-2">
              Your app will be in "Testing" mode initially. To use it:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-sm text-yellow-800">
              <li>Add your email as a test user in the OAuth consent screen</li>
              <li>You can have up to 100 test users</li>
              <li>For production use, you'll need to verify your app with Google (optional)</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Ready to Connect!</h4>
            <p className="text-sm text-green-800">
              Once you've added your credentials in API Settings, you can connect your Google Business Profile
              and start posting updates to your business locations.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
